"use client";

import { useRouter } from "next/navigation";
import { Combobox } from "@/components/shared/combobox";
import { ViewSelector } from "@/components/shared/view-selector";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { TActivity } from "@/types/activity";
import { TComboboxData } from "@/types/combobox-data";
import { TEvent } from "@/types/event";
import { TSettings } from "@/types/settings";
import { Filter, Search } from "lucide-react";
import Image from "next/image";
import { useEffect, useState } from "react";
import {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerTrigger,
} from "../../../components/ui/drawer";
import Whatsapp from "../../../components/shared/whatsapp";
import WANotice from "../../../components/shared/wa-notice";
import ActivitySidebar from "../../../components/shared/activity-sidebar";
import { slugify } from "@/lib/slugify";


export default function ActivitiesContainer({
  defaultActivities,
  defaultEvents,
  settings,
  selectedActivity
}: {
  defaultActivities: TActivity[];
  defaultEvents: TEvent[];
  settings: TSettings;
  selectedActivity?: TActivity
}) {
  const router = useRouter();

  const activityData = defaultActivities
    .map((a) => ({
      value: a.activityCategories[0].id,
      label: a.activityCategories[0].title,
      active: true,
      color: a.activityCategories[0].color,
    }))
    .filter(
      (item, index, self) =>
        index === self.findIndex((t) => t.value === item.value)
    );

  const [activities, setActivities] = useState<TComboboxData[]>(activityData);
  const [currentActivities, setCurrentActivities] = useState(defaultActivities);
  const [search, setSearch] = useState("");

  const [showWANotice, setShowWANotice] = useState(false);

  const [sidebarActivity, setSidebarActivity] = useState<TActivity | null>(
    selectedActivity || null
  );
  const [sidebarOpen, setSidebarOpen] = useState(selectedActivity ? true : false);

  useEffect(() => {
    const localActivities = defaultActivities.filter(
      (a) =>
        activities
          .map((act) => act.value)
          .includes(a.activityCategories[0].id) &&
        (a.title.indexOf(search) !== -1 ||
          a.activityCategories[0].title.indexOf(search) !== -1 ||
          a.displayDescriptionForCategory.join("").indexOf(search) !== -1 ||
          a.activityCategories[0].description.indexOf(search) !== -1)
    );
    setCurrentActivities(localActivities);
  }, [activities, defaultEvents, search]);

  return (
    <>
      <div>
        <div className="w-full min-h-[100vh]">
          <div
            className={
              "flex items-center justify-center gap-2 w-full sm:flex-col mt-2 "
            }
          >
            <ViewSelector />
            <div className="flex gap-4 items-center justify-center sm:mt-8 sm:mb-12">
              <div className="hidden sm:block">
                <Combobox
                  singleSelection={false}
                  label=""
                  hideCloseBtn={true}
                  selectedLabel="Seleciona una actividad"
                  selectedAllLabel="Todas las actividades"
                  data={activityData}
                  placeholder=""
                  notFoundLabel=""
                  onValueChange={(values) => {
                    setActivities(values);
                  }}
                  defaultValues={activities}
                />
              </div>

              <div className="hidden sm:block">
                <div className="relative">
                  <Search
                    stroke="var(--border)"
                    size={20}
                    className="absolute top-2 left-2"
                  />
                  <Input
                    placeholder="Buscar una actividad..."
                    className="pl-8"
                    onChange={(e) => setSearch(e.target.value)}
                    value={search}
                  />
                  <button
                    className={
                      "absolute top-2 right-1 text-secondary-text text-xs cursor-pointer bg-base-bg p-1 " +
                      (search !== "" ? "block" : "hidden")
                    }
                    onClick={() => setSearch("")}
                  >
                    X
                  </button>
                </div>
              </div>

              <Drawer>
                <DrawerTrigger className="block sm:hidden mt-4 mb-4">
                  <Button
                    variant={
                      activities.length < activityData.length || search !== ""
                        ? "default"
                        : "outline"
                    }
                  >
                    <Filter />
                  </Button>
                </DrawerTrigger>
                <DrawerContent>
                  <DrawerClose className="absolute top-3 right-3">
                    <Button variant="outline" className="rounded-full">
                      X
                    </Button>
                  </DrawerClose>
                  <div className="flex flex-col align-center justify-center gap-2 p-4 mt-4 mb-4">
                    <Combobox
                      singleSelection={false}
                      label=""
                      hideCloseBtn={true}
                      selectedLabel="Seleciona una actividad"
                      selectedAllLabel="Todas las actividades"
                      data={activityData}
                      placeholder=""
                      notFoundLabel=""
                      onValueChange={(values) => {
                        setActivities(values);
                      }}
                      defaultValues={activities}
                    />

                    <div className="mt-2 relative">
                      <Search
                        stroke="var(--border)"
                        size={20}
                        className="absolute top-2 left-2"
                      />
                      <Input
                        placeholder="Buscar una actividad..."
                        className="pl-8"
                        onChange={(e) => setSearch(e.target.value)}
                        value={search}
                      />
                      <button>X</button>
                    </div>
                  </div>
                </DrawerContent>
              </Drawer>
            </div>
          </div>

          <div className={showWANotice ? "mb-2 mb-6 sm:mb-12" : ""}>
            <WANotice setShowWANotice={setShowWANotice} callback={() => { }} />
          </div>

          <div className="flex justify-center">
            <div className="mt-4 inline-flex gap-6 flex-wrap cursor-pointer justify-center lg:justify-start">
              {currentActivities.map((activity, key) => (
                <div
                  key={activity.id || key}
                  className="relative sm:w-[45%] lg:w-[30%] min-w-[360px] overflow-hidden radius-base flex flex-row sm:flex-col justify-center"
                >
                  <div className="absolute top-2 right-2 bg-secondary-text text-md font-bold text-muted pt-1 pb-1 pl-3 pr-3 rounded-full">
                    {activity.activityCategories[0].price}€
                  </div>
                  <Image
                    src={activity.image || activity.activityCategories[0].image}
                    width={400}
                    height={300}
                    className="w-full max-w-[30%] sm:max-w-none h-auto object-cover max-h-[300px]"
                    alt=""
                    unoptimized={true}
                  />
                  <div
                    className="p-4 text-center"
                    style={{
                      backgroundColor: activity.activityCategories[0].color,
                    }}
                  >
                    <h2 className="font-domine text-lg font-bold">
                      {activity.displayTitleForCategory[0]}
                    </h2>
                    <p>{activity.subtitle}</p>
                    <div className="p-2 flex justify-center">
                      <Button
                        onClick={() => {
                          setSidebarActivity(activity);
                          setSidebarOpen(true);
                          router.replace("/activities/" + slugify(activity.displayTitleForCategory[0]), { scroll: false });
                        }}
                      >
                        Descubre más y reserva
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            {currentActivities.length === 0 ? (
              <div className="text-secondary-text text-center mt-4 flex flex-col gap-4 items-center">
                <span>
                  No se han encontrado clases con los filtros actuales.
                </span>
                <Button
                  variant={"outline"}
                  className="max-w-[180px]"
                  onClick={() => {
                    setSearch("");
                    setActivities(activityData);
                  }}
                >
                  Reset Filters
                </Button>
              </div>
            ) : (
              <></>
            )}
          </div>
        </div>
      </div>
      <div
        className={
          "fixed top-0 right-0 h-full transform transition-transform duration-500 ease-in-out z-100 ml-4 border-l-1 w-[360px] bg-muted shadom-md overflow-auto " +
          (sidebarOpen ? "translate-x-0" : "translate-x-full")
        }
      >
        <ActivitySidebar
          events={defaultEvents}
          settings={settings}
          activity={sidebarActivity}
          onClose={() => setSidebarOpen(false)}
        />
      </div>
      <div
        className={
          "fixed bottom-3 right-4 rounded-full" +
          (!showWANotice ? "z-99 animate-[bounce-once_1s_ease-out_3]" : "")
        }
      >
        <Whatsapp />
      </div>
    </>
  );
}
